<template>
  <div class="dashboard-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="Maintenance Mode" />
      <main class="page-content">
        <div class="maintenance-page">

          <div class="page-header">
            <div class="page-header-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.78 15.3L19.78 21.3L21.89 19.19L15.89 13.19L13.78 15.3ZM17.5 11.5C17.88 11.5 18.27 11.45 18.64 11.36L17.03 9.75L17.75 8L20.28 7.5L21.17 8.39L19.19 9.19L20.81 10.81L21.72 9.93L22.5 10.72L22 12.33C21.28 12.76 20.45 13 17.5 13C16.83 12.29 16.19 11.61 17.5 11.5ZM10.37 5.56L8 3.18C5.18 3.95 3 6.5 3 9.5C3 13.09 5.91 16 9.5 16C12.5 16 15.05 13.82 15.82 11L13.44 8.63C12.84 10.11 11.28 11.15 9.5 11.15C7.09 11.15 5.15 9.21 5.15 6.8C5.15 5.02 6.19 3.46 7.67 2.86L10.37 5.56Z"/>
              </svg>
            </div>
            <div>
              <h1 class="page-title">Maintenance Mode</h1>
              <p class="page-subtitle">When enabled, all users and agents will see a maintenance banner on every page.</p>
            </div>
          </div>

          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <span>Loading...</span>
          </div>

          <div v-else class="card">

            <!-- Current Status Badge -->
            <div class="status-row">
              <span class="status-label">Current Status</span>
              <span :class="['status-badge', isActive ? 'badge-active' : 'badge-inactive']">
                {{ isActive ? '🔴 MAINTENANCE ON' : '🟢 SYSTEM NORMAL' }}
              </span>
            </div>

            <div v-if="lastUpdated" class="last-updated">
              Last changed: {{ lastUpdated }}
            </div>

            <hr class="divider" />

            <!-- Toggle -->
            <div class="field-row">
              <div class="field-info">
                <div class="field-label">Enable Maintenance Mode</div>
                <div class="field-hint">Turns on the maintenance banner for all users and agents immediately.</div>
              </div>
              <button
                :class="['toggle-btn', isActiveLocal ? 'toggle-on' : 'toggle-off']"
                @click="isActiveLocal = !isActiveLocal"
              >
                <span class="toggle-knob"></span>
              </button>
            </div>

            <!-- Custom Message -->
            <div class="field-group">
              <label class="field-label">Custom Message <span class="optional">(optional)</span></label>
              <div class="field-hint">Leave blank to show the default message. Supports bilingual or any text.</div>
              <textarea
                v-model="customMessage"
                class="message-textarea"
                rows="3"
                placeholder="e.g. We are performing scheduled maintenance. Back in 30 minutes."
              ></textarea>
            </div>

            <!-- Default message preview -->
            <div class="preview-box">
              <div class="preview-label">Preview — what users will see:</div>
              <div class="preview-banner">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.78 15.3L19.78 21.3L21.89 19.19L15.89 13.19L13.78 15.3ZM17.5 11.5C17.88 11.5 18.27 11.45 18.64 11.36L17.03 9.75L17.75 8L20.28 7.5L21.17 8.39L19.19 9.19L20.81 10.81L21.72 9.93L22.5 10.72L22 12.33C21.28 12.76 20.45 13 17.5 13C16.83 12.29 16.19 11.61 17.5 11.5ZM10.37 5.56L8 3.18C5.18 3.95 3 6.5 3 9.5C3 13.09 5.91 16 9.5 16C12.5 16 15.05 13.82 15.82 11L13.44 8.63C12.84 10.11 11.28 11.15 9.5 11.15C7.09 11.15 5.15 9.21 5.15 6.8C5.15 5.02 6.19 3.46 7.67 2.86L10.37 5.56Z"/>
                </svg>
                <span>{{ customMessage || 'System maintenance in progress. Some features may be temporarily unavailable.' }}</span>
              </div>
            </div>

            <!-- Save Button -->
            <div class="actions-row">
              <button class="save-btn" :disabled="saving" @click="save">
                <span v-if="saving">Saving...</span>
                <span v-else>Save Changes</span>
              </button>
              <span v-if="savedMessage" class="saved-indicator">{{ savedMessage }}</span>
            </div>

          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'

export default {
  name: 'AdminMaintenanceView',
  components: { Sidebar, TopHeader },
  data () {
    return {
      loading: true,
      saving: false,
      isActive: false,
      isActiveLocal: false,
      customMessage: '',
      lastUpdated: null,
      savedMessage: ''
    }
  },
  async mounted () {
    await this.loadSettings()
  },
  methods: {
    async loadSettings () {
      this.loading = true
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/admin-maintenance', {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
        })
        const data = await response.json()
        if (data.success) {
          this.isActive = !!data.is_active
          this.isActiveLocal = !!data.is_active
          this.customMessage = data.message || ''
          this.lastUpdated = data.updated_at
            ? new Date(data.updated_at).toLocaleString()
            : null
        }
      } catch (e) {
        console.error('Failed to load maintenance settings', e)
      } finally {
        this.loading = false
      }
    },

    async save () {
      this.saving = true
      this.savedMessage = ''
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/admin-maintenance', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            is_active: this.isActiveLocal,
            message: this.customMessage.trim() || null
          })
        })
        const result = await response.json()
        if (result.success) {
          this.isActive = this.isActiveLocal
          this.lastUpdated = new Date().toLocaleString()
          this.savedMessage = '✓ Saved'
          setTimeout(() => { this.savedMessage = '' }, 3000)
        }
      } catch (e) {
        console.error('Failed to save maintenance settings', e)
        this.savedMessage = '✗ Failed to save'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.dashboard-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary);
}
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.page-content {
  flex: 1;
  padding: 32px;
  overflow-y: auto;
}
.maintenance-page {
  max-width: 680px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
}
.page-header-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}
.page-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

/* Loading */
.loading-state {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 48px;
  justify-content: center;
  color: var(--text-secondary);
}
.spinner {
  width: 22px;
  height: 22px;
  border: 3px solid var(--border-primary);
  border-top-color: #d97706;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Card */
.card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 2px 12px var(--shadow-primary);
}

/* Status */
.status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.status-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
}
.status-badge {
  font-size: 13px;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 20px;
  letter-spacing: 0.3px;
}
.badge-active {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fca5a5;
}
.badge-inactive {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #86efac;
}
.last-updated {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 20px;
}

.divider {
  border: none;
  border-top: 1px solid var(--border-primary);
  margin: 20px 0;
}

/* Toggle row */
.field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}
.field-info {
  flex: 1;
}
.field-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}
.field-hint {
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}
.optional {
  font-weight: 400;
  font-size: 13px;
  color: var(--text-secondary);
}

/* Toggle button */
.toggle-btn {
  position: relative;
  width: 52px;
  height: 28px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  transition: background 0.25s;
  flex-shrink: 0;
  padding: 0;
}
.toggle-off { background: #d1d5db; }
.toggle-on  { background: #d97706; }
.toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  transition: transform 0.25s;
}
.toggle-on .toggle-knob { transform: translateX(24px); }

/* Message textarea */
.field-group {
  margin-bottom: 24px;
}
.message-textarea {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  margin-top: 6px;
}
.message-textarea:focus {
  border-color: #d97706;
  box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1);
}

/* Preview */
.preview-box {
  margin-bottom: 28px;
}
.preview-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}
.preview-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  color: #fff;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
}

/* Actions */
.actions-row {
  display: flex;
  align-items: center;
  gap: 16px;
}
.save-btn {
  padding: 10px 28px;
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(217, 119, 6, 0.25);
}
.save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(217, 119, 6, 0.35);
}
.save-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.saved-indicator {
  font-size: 14px;
  font-weight: 600;
  color: #16a34a;
}
</style>
